/**
 * Where a descriptor's fields live in an environment's `<env>/values.yaml`
 * (appenv repository, branch `main`), how they are read from there, and how
 * a change is written back — platform-catalog `contracts/README.md`,
 * « Lecture » and « Écriture ».
 *
 * WRITTEN IN PLACE: the file is commented at length — why each value, what
 * the chart does with it, the incidents behind a setting — and may carry
 * keys and comments of an administrator's own. It is edited through the
 * `yaml` package's Document API: only the scalars that change are touched,
 * and `yaml` keeps a scalar's node — its comments, its quoting — when its
 * value is replaced (`YAMLMap.add` of yaml 2.9: « For scalars, keep the old
 * node & its comments and anchors »).
 *
 * READ STRICTLY: a value that cannot be read refuses any change, every
 * unreadable value said (`problems`). So the path of every configurable
 * field exists when the file is written, and `setIn` replaces each value
 * where it is; only the keys that may be missing (the CloudNativePG mode's
 * and replication's, the derived ones) are created, at the end of their
 * block, in the order of the README.
 *
 * THE KEYS DERIVED from the configuration are written only when what they
 * derive from changes, so that an administrator's hand-tuning of them
 * survives an unrelated change:
 *  - the mode (`databaseMode`) → `instances`, and `podAntiAffinityType` /
 *    `topologyKey` as the appenv skeleton writes them for that mode, with
 *    the skeleton's comment — only when the MODE changes: within the same
 *    mode an explicit override (odooperf1's `preferred`) is left alone;
 *  - the mode or the synchronous replication (`databaseSync`) →
 *    `minSyncReplicas` (1 with three instances) and `maxSyncReplicas`;
 *  - each `derived` of the descriptor, when one of its triggers changes.
 *
 * THE VALKEY BLOCK UNDER ITS OLD NAME (Valkey renaming plan, 2026-10-25):
 * during the alias period, an environment's values may hold a `valkey`
 * component's block under its other name (`odoo.redis` for a descriptor
 * saying `odoo.valkey`, `valuesPathAlias`): waadoo-common 0.4.x reads a
 * non-empty `redis:` over its `valkey:` defaults (`waadoo-common.valkey.values`
 * of `_valkey.tpl`). Such a block is read AND written where it is
 * (`valuesBlockPaths`), never moved: the chart the environment deploys is
 * pinned in the apps repository (`deploy/Chart.yaml`), out of this file's
 * sight, and a chart older than the alias (odoo < 1.2.0, wagtail < 0.3.0)
 * reads `redis:` only — a block renamed here would silently turn its Valkey
 * off. The block is renamed by the project's migration, with its chart.
 * Both blocks at once are refused: what the chart then deploys depends on
 * both (the old one over the new, a refusal to render when the new one is
 * enabled), not on the one a field reads. An empty block (`redis: {}`) is
 * no block, as for the chart. With NO block at all, a value is written at
 * the descriptor's path: a migrated descriptor (`odoo.valkey`) over a
 * project still on a chart older than the alias would then write a block
 * that chart ignores, its cache staying off without a word. The skeletons
 * always render the block, so this only matters for a hand-edited appenv.
 */

import { isMap, isScalar, parseDocument, type Document } from 'yaml';
import {
  DATABASE_MODES,
  MODE_INSTANCES,
  RESOURCE_KEYS,
  type AppTypeDescriptor,
  type DatabaseMode,
  type DescriptorField,
  type FieldValue,
  type FieldValues,
  type QuantityUnit,
  type ResourceKey,
  valueOf,
} from './descriptor';
import { asText, evaluate, hasOwn } from './expression';

type Path = readonly string[];

export function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

export function at(values: unknown, path: Path): unknown {
  let node: unknown = values;
  for (const key of path) {
    const map = record(node);
    // Own keys only: `constructor` of a map is not a value of the file.
    node = map && hasOwn(map, key) ? map[key] : undefined;
  }
  return node;
}

const UNIT_FACTORS: Readonly<Record<QuantityUnit, Record<string, number>>> = {
  // A Kubernetes CPU quantity in millicores.
  m: { m: 1, '': 1000 },
  // A memory quantity in Mi.
  Mi: { Ki: 1 / 1024, Mi: 1, Gi: 1024, Ti: 1024 * 1024, '': 1 / 1024 ** 2 },
  // A volume size in Gi.
  Gi: { Mi: 1 / 1024, Gi: 1, Ti: 1024 },
};

/**
 * A Kubernetes quantity in `unit` (`m`, `Mi`, `Gi`), or undefined when it is
 * not one, or not a whole number of that unit (`1.5Gi` of a volume, `100M`
 * of memory: decimal units are not read).
 */
export function quantityIn(
  value: unknown,
  unit: QuantityUnit,
): number | undefined {
  const text =
    typeof value === 'number' && Number.isFinite(value) ? String(value) : value;
  if (typeof text !== 'string') {
    return undefined;
  }
  const match = /^([0-9]+(?:\.[0-9]+)?)([A-Za-z]*)$/.exec(text.trim());
  if (!match) {
    return undefined;
  }
  const factors = UNIT_FACTORS[unit];
  const factor = hasOwn(factors, match[2]) ? factors[match[2]] : undefined;
  if (factor === undefined) {
    return undefined;
  }
  const amount = Number(match[1]) * factor;
  return Number.isInteger(amount) && amount >= 0 ? amount : undefined;
}

export function wholeNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : undefined;
}

/** A parsed `values.yaml`, or why it could not be. */
export function parseValues(
  content: string,
): { values: unknown } | { error: string } {
  const doc = parseDocument(content);
  if (doc.errors.length > 0) {
    return { error: doc.errors[0].message };
  }
  return { values: doc.toJS() };
}

/**
 * Helm's merge of two values files: maps merged key by key, `over` winning on
 * anything else (lists and scalars replaced).
 */
export function mergeValues(base: unknown, over: unknown): unknown {
  const a = record(base);
  const b = record(over);
  if (!a || !b) {
    return over === undefined ? base : over;
  }
  const merged: Record<string, unknown> = { ...a };
  for (const [key, value] of Object.entries(b)) {
    // Defined, not assigned: a key `__proto__` stays a key.
    Object.defineProperty(merged, key, {
      value: mergeValues(hasOwn(a, key) ? a[key] : undefined, value),
      enumerable: true,
      writable: true,
      configurable: true,
    });
  }
  return merged;
}

const describe = (path: Path) => path.join('.');
const shown = (raw: unknown) => JSON.stringify(raw ?? null);

/** Where a descriptor's paths are in some values (`valuesBlockPaths`). */
export interface ValuesBlockPaths {
  /** `path` where the values hold it: unchanged, or under the alias. */
  locate(path: Path): Path;
  /** In French, one per component whose block is there under both names. */
  problems: string[];
}

/** Whether `value` is a block with at least one key (header). */
function isBlock(value: unknown): boolean {
  const map = record(value);
  return map !== undefined && Object.keys(map).length > 0;
}

function isPrefix(prefix: Path, path: Path): boolean {
  return (
    prefix.length <= path.length &&
    prefix.every((key, index) => path[index] === key)
  );
}

/**
 * Where the paths of `descriptor` are in `values`: those under a `valkey`
 * component's `valuesPath` move under its `valuesPathAlias` when the values
 * hold the block there and not at `valuesPath` (header); any other path is
 * unchanged. A component whose block is under both names is a problem, its
 * paths left at `valuesPath`.
 */
export function valuesBlockPaths(
  descriptor: AppTypeDescriptor,
  values: unknown,
): ValuesBlockPaths {
  const moved: Array<{ from: Path; to: Path }> = [];
  const problems: string[] = [];
  for (const component of descriptor.components) {
    const alias = component.valuesPathAlias;
    if (!alias) continue;
    const here = isBlock(at(values, component.valuesPath));
    const there = isBlock(at(values, alias));
    if (here && there) {
      problems.push(
        `${describe(component.valuesPath)} et ${describe(
          alias,
        )} tous deux présents : un seul bloc attendu (${describe(
          component.valuesPath,
        )}, mêmes clés).`,
      );
    } else if (there) {
      moved.push({ from: component.valuesPath, to: alias });
    }
  }
  return {
    locate: path => {
      const block = moved.find(item => isPrefix(item.from, path));
      return block ? [...block.to, ...path.slice(block.from.length)] : path;
    },
    problems,
  };
}

/**
 * One plain field (`integer`, `enum`, `boolean`, `quantity`) at its path,
 * or the problem that says why not.
 */
function readPlain(
  field: DescriptorField,
  values: unknown,
  paths: ValuesBlockPaths,
): { value: FieldValue } | { problem: string } {
  const path = paths.locate(field.path);
  const raw = at(values, path);
  const where = describe(path);
  // A key that is not in the file at all is not « unreadable »: it is the
  // usual state of an environment created before its type gained the field
  // (opencloud's preprod and prod, 2026-10-29). Said as such, still refused.
  if (raw === undefined) {
    return {
      problem: `${where} absent du fichier (champ ajouté au type après la création de l’environnement ?) : à ajouter avec la valeur voulue.`,
    };
  }
  switch (field.type) {
    case 'quantity': {
      const unit = field.unit as QuantityUnit;
      const value = quantityIn(raw, unit);
      return value === undefined
        ? {
            problem: `${where} illisible (${shown(
              raw,
            )}) : attendu un nombre entier de ${unit}.`,
          }
        : { value };
    }
    case 'boolean':
      return typeof raw === 'boolean'
        ? { value: raw }
        : {
            problem: `${where} illisible (${shown(
              raw,
            )}) : attendu true ou false.`,
          };
    case 'enum':
      if (!field.numericEnum) {
        return typeof raw === 'string'
          ? { value: raw }
          : {
              problem: `${where} illisible (${shown(
                raw,
              )}) : attendu une chaîne de caractères.`,
            };
      }
      break;
    default:
      break;
  }
  const value = wholeNumber(raw);
  return value === undefined
    ? {
        problem: `${where} illisible (${shown(
          raw,
        )}) : attendu un nombre entier.`,
      }
    : { value };
}

/** The anti-affinity, zones and replication keys of a CloudNativePG block. */
const CNPG_KEYS = {
  instances: 'instances',
  antiAffinity: 'podAntiAffinityType',
  topology: 'topologyKey',
  minSync: 'minSyncReplicas',
  maxSync: 'maxSyncReplicas',
} as const;

/** The zone label the skeleton spreads PostgreSQL instances on. */
export const ZONE_TOPOLOGY_KEY = 'topology.kubernetes.io/zone';

/**
 * The comment the appenv skeleton writes above `podAntiAffinityType`, per
 * mode — the leading space of each line is `yaml`'s, the `#` is not stored.
 */
const ANTI_AFFINITY_COMMENTS: Readonly<Record<'single' | 'ha', string>> = {
  ha: [
    ' Une instance par zone : anti-affinite obligatoire sur le label de zone,',
    ' donc aussi sur des noeuds distincts. Avec 2 instances, une anti-affinite',
    ' "preferred" sur le noeud ne suffisait pas : les deux instances',
    " d'odooperf1 se sont retrouvees sur le meme noeud (ludo, 2026-10-14). Les",
    ' volumes Cinder zonaux (WaitForFirstConsumer) suivent leur instance.',
  ].join('\n'),
  single: [
    ' Une seule instance : aucune anti-affinite a respecter ("" = le defaut',
    ' de CloudNativePG, kubernetes.io/hostname).',
  ].join('\n'),
};

export interface ConfigurationReading {
  /** Absent when a field could not be read: `problems` says which. */
  configuration?: FieldValues;
  /** In French, one per field that could not be read. */
  problems: string[];
}

/** The rank of each type in the reading order (`readConfiguration`). */
const READ_RANK: Readonly<Record<DescriptorField['type'], number>> = {
  quantity: 0,
  integer: 1,
  enum: 1,
  boolean: 1,
  databaseMode: 2,
  databaseSync: 3,
};

/**
 * The configurable fields read from the values of one or more files, already
 * merged (an ephemeral branch's are `dev/values.yaml` under
 * `ephemeral-template/values.yaml`, `mergeValues`). The problems come in
 * the groups of the code this engine replaced — the quantities, then the
 * plain numbers and booleans, then the mode, then the replication —, each
 * group in the descriptor's order (that code had its own order within a
 * group: the same texts, possibly in another order, when several fields of a
 * group cannot be read).
 */
export function readConfiguration(
  descriptor: AppTypeDescriptor,
  values: unknown,
): ConfigurationReading {
  const paths = valuesBlockPaths(descriptor, values);
  const problems: string[] = [...paths.problems];
  const read: FieldValues = {};
  const fields = [...descriptor.configurableFields].sort(
    (a, b) => READ_RANK[a.type] - READ_RANK[b.type],
  );
  let mode: DatabaseMode | undefined;
  for (const field of fields) {
    if (field.type === 'databaseMode') {
      const path = paths.locate([...field.path, CNPG_KEYS.instances]);
      const instances = at(values, path);
      mode = DATABASE_MODES.find(item => MODE_INSTANCES[item] === instances);
      if (mode) {
        read[field.id] = mode;
      } else {
        problems.push(
          `${describe(path)} illisible (${shown(
            instances,
          )}) : attendu 1, 2 ou 3.`,
        );
      }
      continue;
    }
    if (field.type === 'databaseSync') {
      const path = paths.locate([...field.path, CNPG_KEYS.maxSync]);
      const maxSync = wholeNumber(at(values, path) ?? 0);
      if (maxSync === undefined) {
        problems.push(
          `${describe(path)} illisible : attendu un nombre entier.`,
        );
      } else {
        read[field.id] = maxSync > 0 && mode !== undefined && mode !== 'single';
      }
      continue;
    }
    const result = readPlain(field, values, paths);
    if ('problem' in result) {
      problems.push(result.problem);
    } else {
      read[field.id] = result.value;
    }
  }
  return problems.length > 0 ? { problems } : { configuration: read, problems };
}

/** Millicores and Mi of one container, by resource. */
export type ContainerResources = Record<ResourceKey, number>;

/** What the quota reads beyond the configuration (README, Quota). */
export interface QuotaInputs {
  /** The non-configurable fields: the quota's own entries (Odoo: Mailpit). */
  values: FieldValues;
  /** Each extra with `resourcesFrom`, by id. */
  extras: Record<string, ContainerResources>;
}

/** The side and resource of each figure under `resourcesFrom`. */
const RESOURCE_PATHS: ReadonlyArray<
  [ResourceKey, 'requests' | 'limits', 'cpu' | 'memory']
> = [
  ['cpuRequest', 'requests', 'cpu'],
  ['memoryRequest', 'requests', 'memory'],
  ['cpuLimit', 'limits', 'cpu'],
  ['memoryLimit', 'limits', 'memory'],
];

/**
 * The quota's entries read from an environment's values, strictly (its
 * `quota.yaml` exists: they size it), or why they cannot be read: the
 * non-configurable fields at their path, then each extra's `resourcesFrom`
 * (`requests.cpu`, `requests.memory`, `limits.cpu`, `limits.memory`).
 */
export function readQuotaInputs(
  descriptor: AppTypeDescriptor,
  values: unknown,
): QuotaInputs | { problems: string[] } {
  const paths = valuesBlockPaths(descriptor, values);
  const problems: string[] = [...paths.problems];
  const read: QuotaInputs = { values: {}, extras: {} };
  for (const field of descriptor.fields.filter(item => !item.configurable)) {
    const result = readPlain(field, values, paths);
    if ('problem' in result) {
      problems.push(result.problem);
    } else {
      read.values[field.id] = result.value;
    }
  }
  for (const extra of descriptor.quotaExtras) {
    if (!extra.resourcesFrom) continue;
    const from = paths.locate(extra.resourcesFrom);
    const resources = record(at(values, from));
    const figures = { ...extra.resources };
    for (const [key, side, resource] of RESOURCE_PATHS) {
      const raw = record(resources?.[side])?.[resource];
      const unit = resource === 'cpu' ? 'm' : 'Mi';
      const value = quantityIn(raw, unit);
      if (value === undefined) {
        problems.push(
          `${describe([...from, side, resource])} illisible (${shown(
            raw,
          )}) : attendu un nombre entier de ${unit}.`,
        );
      } else {
        figures[key] = value;
      }
    }
    read.extras[extra.id] = figures;
  }
  return problems.length > 0 ? { problems } : read;
}

/**
 * `readQuotaInputs`, tolerant: for an appenv older than the quotas (no
 * `quota.yaml`), where these values only feed the cluster's ceiling. When
 * anything cannot be read, the readable fields are kept and the others take
 * their `default`, and EVERY extra takes its `resources` — the code this
 * engine replaced fell back on the skeleton's init Job as a whole
 * (`quotaExtrasOrDefaults`), whatever was unreadable.
 */
export function quotaInputsOrDefaults(
  descriptor: AppTypeDescriptor,
  values: unknown,
): QuotaInputs {
  const strict = readQuotaInputs(descriptor, values);
  if (!('problems' in strict)) {
    return strict;
  }
  const paths = valuesBlockPaths(descriptor, values);
  const tolerant: QuotaInputs = { values: {}, extras: {} };
  for (const field of descriptor.fields.filter(item => !item.configurable)) {
    const result = readPlain(field, values, paths);
    const value = 'value' in result ? result.value : field.default;
    if (value !== undefined) {
      tolerant.values[field.id] = value;
    }
  }
  return tolerant;
}

/** The resources of a container from four figures. */
export function containerOf(
  figures: Partial<Record<ResourceKey, number>>,
  defaults: ContainerResources,
): ContainerResources {
  const out = { ...defaults };
  for (const key of RESOURCE_KEYS) {
    const value = figures[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * Sets one scalar, keeping its node when there is one (header). A string that
 * is empty is written quoted, as the skeleton's `topologyKey: ""`; any other
 * string the node already quoted stays quoted, a new one is plain.
 */
function setScalar(doc: Document, path: Path, value: FieldValue) {
  const existing = doc.getIn(path, true);
  const wasEmpty = isScalar(existing) && existing.value === '';
  doc.setIn(path, value);
  if (isScalar(existing) && typeof value === 'string') {
    if (value === '') {
      existing.type = 'QUOTE_DOUBLE';
    } else if (wasEmpty) {
      // `""` turned into a word: written plain, as the skeleton does.
      existing.type = 'PLAIN';
    }
  }
}

/** The comment before a key of a map, replaced (the anti-affinity's). */
function setKeyComment(doc: Document, path: Path, comment: string) {
  const parent = doc.getIn(path.slice(0, -1), true);
  if (!isMap(parent)) {
    return;
  }
  const pair = parent.items.find(
    item => isScalar(item.key) && item.key.value === path[path.length - 1],
  );
  if (pair && isScalar(pair.key)) {
    pair.key.commentBefore = comment;
  }
}

/**
 * `content` with `current` turned into `desired`: the fields that differ,
 * and the keys derived from them (header), in the order of the README
 * (« Écriture »). Throws on a file `yaml` cannot parse — the caller read the
 * same content first.
 */
export function writeConfiguration(
  descriptor: AppTypeDescriptor,
  content: string,
  current: FieldValues,
  desired: FieldValues,
): string {
  const doc = parseDocument(content);
  if (doc.errors.length > 0) {
    throw new Error(`values.yaml illisible : ${doc.errors[0].message}`);
  }
  const changed = (id: string) => valueOf(desired, id) !== valueOf(current, id);
  const fields = descriptor.configurableFields;
  // Where the file holds each path (header: a Valkey block under its old
  // name is written there).
  const { locate } = valuesBlockPaths(descriptor, doc.toJS());

  for (const field of fields) {
    if (field.type === 'databaseMode' || field.type === 'databaseSync') {
      continue;
    }
    if (changed(field.id)) {
      const value = desired[field.id];
      setScalar(
        doc,
        locate(field.path),
        field.type === 'quantity' ? `${value}${field.unit}` : value,
      );
    }
  }

  const modeField = fields.find(field => field.type === 'databaseMode');
  const syncField = fields.find(field => field.type === 'databaseSync');
  if (modeField) {
    const block = locate(modeField.path);
    const mode = desired[modeField.id] as DatabaseMode;
    const antiAffinity = [...block, CNPG_KEYS.antiAffinity];
    if (changed(modeField.id)) {
      setScalar(doc, [...block, CNPG_KEYS.instances], MODE_INSTANCES[mode]);
      const single = mode === 'single';
      setScalar(doc, antiAffinity, single ? 'preferred' : 'required');
      setScalar(
        doc,
        [...block, CNPG_KEYS.topology],
        single ? '' : ZONE_TOPOLOGY_KEY,
      );
      setKeyComment(
        doc,
        antiAffinity,
        ANTI_AFFINITY_COMMENTS[single ? 'single' : 'ha'],
      );
    }
    if (syncField && (changed(modeField.id) || changed(syncField.id))) {
      const sync = desired[syncField.id] === true;
      setScalar(
        doc,
        [...block, CNPG_KEYS.minSync],
        sync && mode === 'ha3zones' ? 1 : 0,
      );
      setScalar(doc, [...block, CNPG_KEYS.maxSync], sync ? 1 : 0);
    }
  }

  for (const item of descriptor.derived) {
    if (!item.triggers.some(changed)) {
      continue;
    }
    const value = evaluate(item.value, { values: desired });
    setScalar(
      doc,
      locate(item.path),
      item.format === undefined
        ? value
        : item.format.replace('{value}', asText(value)),
    );
  }
  return doc.toString();
}

/**
 * Whether `values` hold an explicit anti-affinity other than the skeleton's
 * for their mode — odooperf1's `preferred` on two instances. Said by the
 * report when a change of mode replaces it.
 */
export function antiAffinityOverride(
  descriptor: AppTypeDescriptor,
  values: unknown,
): boolean {
  const modeField = descriptor.configurableFields.find(
    field => field.type === 'databaseMode',
  );
  if (!modeField) {
    return false;
  }
  const block = valuesBlockPaths(descriptor, values).locate(modeField.path);
  const instances = at(values, [...block, CNPG_KEYS.instances]);
  const type = at(values, [...block, CNPG_KEYS.antiAffinity]);
  return typeof instances === 'number' && instances > 1 && type !== 'required';
}
